import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@r/utils/utils.ts';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';

const baseStyles = {
  base: [
    'peer',
    'flex',
    'border',
    'font-light',
    'placeholder:font-light',
    'ring-offset-background',
    'bg-background',
    'border-input',
    'placeholder:text-muted-foreground',
    'rounded-md'
  ],
  file: [
    'file:border-0',
    'file:bg-transparent',
    'file:text-sm',
    'file:font-medium',
    'file:text-foreground'
  ],
  focus: ['focus-visible:outline-none'],
  disabled: ['disabled:cursor-not-allowed', 'disabled:opacity-30']
};

const variantsStyles = {
  default: ['focus-visible:border-primary'],
  error: ['border-destructive', 'text-destructive', 'placeholder:text-destructive']
};

const inputVariants = cva(
  [...baseStyles.base, ...baseStyles.file, ...baseStyles.focus, ...baseStyles.disabled].join(' '),
  {
    variants: {
      variant: {
        default: [...variantsStyles.default].join(' '),
        error: [...variantsStyles.error].join(' ')
      },
      inputSize: {
        default: 'text-sm h-[2.5rem] w-full px-3 file:leading-[2.5rem]',
        sm: 'text-xs h-[1.875rem] file:leading-[1.875rem] px-2',
        md: 'text-sm h-[2.625rem] file:leading-[2.625rem] px-3',
        lg: 'text-lg h-[3.125rem] file:leading-[3.125rem] px-4'
      }
    },
    defaultVariants: {
      variant: 'default',
      inputSize: 'default'
    }
  }
);

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  variant?: 'default' | 'error';
  disabled?: boolean;
  placeholder?: string;
  backgroundColor?: string;
  color?: string;
  inputSize?: 'default' | 'sm' | 'md' | 'lg';
  asChild?: boolean;
  error?: boolean;
  suffix?: ReactNode;
  clear?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error,
      className,
      variant,
      inputSize,
      backgroundColor,
      color,
      type,
      suffix,
      clear,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    if (error) {
      variant = 'error';
    }

    const hasClear =
      Boolean(clear) && typeof value === 'string' && value !== undefined && value.length > 0;
    const paddingRightClass = cn(
      (hasClear && suffix && 'pr-14') || ((hasClear || suffix) && 'pr-8') || undefined
    );
    const handleClear = () => {
      if (onChange) {
        onChange({ target: { value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    };

    return (
      <div className="flex relative items-center">
        <input
          type={type}
          className={cn(
            inputVariants({ variant, inputSize, className }),
            paddingRightClass // Ajoute du padding à droite si un suffixe/clear est présent
          )}
          style={{ backgroundColor, color }}
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
        />
        {hasClear && (
          <button
            type="button"
            aria-label="Effacer"
            className={cn(
              'absolute text-muted-foreground hover:text-foreground transition-colors',
              suffix ? 'right-8' : 'right-3'
            )}
            onClick={handleClear}
            tabIndex={-1}
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {suffix && <div className="absolute right-3 text-muted-foreground">{suffix}</div>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
